import { useContext, useEffect, useRef } from 'react';
import { ApiClientService, AuthContext, useApiClient } from '../features';
export const useJobChecker = () => {
    const { entityId } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const intervalId = useRef();
    /**
     * Clean up the interval when the component is unmounted
     */
    useEffect(() => {
        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
            }
        };
    }, []);
    /**
     * Retrieves the current status of a job.
     * @param jobId Id of the job to get the status of.
     * @returns The workflow object representing the status of the job.
     */
    async function getJobStatus(jobId) {
        const { data: statusResponse } = await momClient.get(`/v1/entities/${entityId}/workflows/${jobId}`);
        return statusResponse.data;
    }
    /**
     * Repeatedly checks the status of a job until it is completed.
     * @param jobId Id of the job to check the status of.
     * @param options Optional parameters for the check.
     * @param options.interval The interval in milliseconds to check the status of the job. Defaults to 1000.
     * @param options.onInterval A callback function to call at every interval when the status of the job is checked.
     * @returns The workflow object representing the status of the job.
     */
    async function checkJobStatus(jobId, { interval = 1000, onInterval, } = {}) {
        const initialJobResponse = await getJobStatus(jobId);
        if (initialJobResponse.status === 'created' || initialJobResponse.status === 'active') {
            return await new Promise((resolve) => {
                const endPolling = (response) => {
                    clearInterval(intervalId.current);
                    if (response) {
                        resolve(response);
                    }
                };
                intervalId.current = setInterval(async () => {
                    const jobResponse = await getJobStatus(jobId);
                    if (onInterval) {
                        onInterval(jobId, jobResponse, endPolling);
                    }
                    if (jobResponse.status !== 'created' && jobResponse.status !== 'active') {
                        endPolling(jobResponse);
                    }
                }, interval);
            });
        }
        return initialJobResponse;
    }
    return { checkJobStatus, getJobStatus };
};
